// Note Export Utility Service
// Exports notes in multiple formats: TXT, JSON, Markdown, PDF, DOCX

import { jsPDF } from 'jspdf';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, ExternalHyperlink } from 'docx';
import { saveAs } from 'file-saver';
import { convert } from 'html-to-text';

/**
 * Sanitize filename by removing invalid characters
 * @param {string} name - The filename to sanitize
 * @returns {string} - Sanitized filename
 */
function sanitizeFilename(name) {
    if (!name) return 'note';
    return name
        .replace(/[<>:"/\\|?*]/g, '') // Remove invalid chars
        .replace(/\s+/g, '_')         // Replace spaces with underscores
        .substring(0, 100)            // Limit length
        .trim() || 'note';
}

/**
 * Generate a meaningful filename from note data
 * @param {object} note - The note object
 * @returns {string} - Generated filename (without extension)
 */
function generateFilename(note) {
    // Prefer title if available
    if (note.title && note.title.trim()) {
        return sanitizeFilename(note.title);
    }
    
    // Try to extract domain from URL
    const url = note.url || note.key;
    if (url && url.startsWith('http')) {
        try {
            const urlObj = new URL(url);
            const domain = urlObj.hostname.replace(/^www\./, '');
            const path = urlObj.pathname.replace(/\//g, '_').replace(/^_|_$/g, '');
            if (path && path !== '_') {
                return sanitizeFilename(`${domain}_${path}`);
            }
            return sanitizeFilename(domain);
        } catch {
            // Fall through to default
        }
    }
    
    // Use domain if available
    if (note.domain) {
        return sanitizeFilename(note.domain);
    }
    
    // Default with timestamp
    return `note_${Date.now()}`;
}

/**
 * Parse TipTap JSON content to plain text
 * @param {object} doc - TipTap document object
 * @returns {string} - Plain text content
 */
function tiptapToPlainText(doc) {
    if (!doc || !doc.content) return '';
    
    let text = '';
    
    function processNode(node, prefix = '') {
        if (node.type === 'text') {
            return node.text || '';
        }
        
        if (node.type === 'hardBreak') {
            return '\n';
        }
        
        if (node.type === 'paragraph') {
            const content = node.content ? node.content.map(n => processNode(n)).join('') : '';
            return content + '\n\n';
        }
        
        if (node.type === 'heading') {
            const level = node.attrs?.level || 1;
            const content = node.content ? node.content.map(n => processNode(n)).join('') : '';
            return '#'.repeat(level) + ' ' + content + '\n\n';
        }
        
        if (node.type === 'bulletList' || node.type === 'orderedList') {
            const items = node.content || [];
            return items.map((item, index) => {
                const marker = node.type === 'orderedList' ? `${index + 1}. ` : '• ';
                const content = item.content ? item.content.map(n => processNode(n, '  ')).join('') : '';
                return marker + content.trim();
            }).join('\n') + '\n\n';
        }
        
        if (node.type === 'listItem') {
            const content = node.content ? node.content.map(n => processNode(n)).join('') : '';
            return content;
        }
        
        if (node.type === 'codeBlock') {
            const content = node.content ? node.content.map(n => processNode(n)).join('') : '';
            return '```\n' + content + '\n```\n\n';
        }
        
        if (node.type === 'blockquote') {
            const content = node.content ? node.content.map(n => processNode(n)).join('') : '';
            return '> ' + content.replace(/\n/g, '\n> ').trim() + '\n\n';
        }
        
        if (node.type === 'horizontalRule') {
            return '---\n\n';
        }
        
        if (node.type === 'table') {
            // Simple table handling
            const rows = node.content || [];
            return rows.map(row => {
                const cells = row.content || [];
                return '| ' + cells.map(cell => {
                    const content = cell.content ? cell.content.map(n => processNode(n)).join('') : '';
                    return content.trim();
                }).join(' | ') + ' |';
            }).join('\n') + '\n\n';
        }
        
        // Default: process children
        if (node.content) {
            return node.content.map(n => processNode(n, prefix)).join('');
        }
        
        return '';
    }
    
    text = processNode(doc);
    return text.trim();
}

/**
 * Convert note content to plain text
 * @param {any} content - Note content (can be string, HTML, or TipTap JSON)
 * @returns {string} - Plain text content
 */
function getPlainTextContent(content) {
    if (!content) return '';
    
    // Handle string content
    if (typeof content === 'string') {
        try {
            const parsed = JSON.parse(content);
            // TipTap v2 format
            if (parsed.version === 2 && parsed.content) {
                return tiptapToPlainText(parsed.content);
            }
            // Direct TipTap doc
            if (parsed.type === 'doc') {
                return tiptapToPlainText(parsed);
            }
        } catch {
            // It's HTML or plain text
            if (content.trim().startsWith('<')) {
                // Convert HTML to text
                return convert(content, {
                    wordwrap: false,
                    preserveNewlines: true,
                });
            }
            return content;
        }
    }
    
    // Handle object content (TipTap JSON)
    if (typeof content === 'object') {
        if (content.version === 2 && content.content) {
            return tiptapToPlainText(content.content);
        }
        if (content.type === 'doc') {
            return tiptapToPlainText(content);
        }
    }
    
    return '';
}

/**
 * Format timestamp for display
 * @param {any} timestamp - Firestore timestamp or Date
 * @returns {string} - Formatted date string
 */
function formatTimestamp(timestamp) {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
}

/**
 * Get note metadata string
 * @param {object} note - Note object
 * @returns {string} - Formatted metadata
 */
function getNoteMetadata(note) {
    const url = note.url || note.key || 'N/A';
    const title = note.title || note.domain || 'Untitled';
    const created = formatTimestamp(note.createdAt);
    const updated = formatTimestamp(note.updatedAt);
    
    return `URL: ${url}\nTitle: ${title}\nCreated: ${created}\nLast Updated: ${updated}`;
}

/**
 * Export note as TXT file
 * @param {object} note - Note object
 */
export function exportAsTxt(note) {
    const filename = generateFilename(note) + '.txt';
    const metadata = getNoteMetadata(note);
    const content = getPlainTextContent(note.content);
    
    const fullContent = `${metadata}\n\n${'='.repeat(50)}\n\n${content}`;
    
    const blob = new Blob([fullContent], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, filename);
}

/**
 * Export note as JSON file
 * @param {object} note - Note object
 */
export function exportAsJson(note) {
    const filename = generateFilename(note) + '.json';
    
    const exportData = {
        url: note.url || note.key,
        title: note.title || note.domain || 'Untitled',
        domain: note.domain,
        content: note.content,
        plainTextContent: getPlainTextContent(note.content),
        createdAt: note.createdAt?.toDate?.() || note.createdAt,
        updatedAt: note.updatedAt?.toDate?.() || note.updatedAt,
        isPageNote: note.isPageNote,
        isFavorite: note.isFavorite,
        isPinned: note.isPinned,
        folderId: note.folderId,
        tagIds: note.tagIds,
        exportedAt: new Date().toISOString(),
    };
    
    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
    saveAs(blob, filename);
}

/**
 * Convert note content to Markdown
 * @param {any} content - Note content
 * @returns {string} - Markdown content
 */
function contentToMarkdown(content) {
    if (!content) return '';
    
    // Handle string content
    if (typeof content === 'string') {
        try {
            const parsed = JSON.parse(content);
            if (parsed.version === 2 && parsed.content) {
                return tiptapToMarkdown(parsed.content);
            }
            if (parsed.type === 'doc') {
                return tiptapToMarkdown(parsed);
            }
        } catch {
            // HTML or plain text - use plain text conversion
            if (content.trim().startsWith('<')) {
                return convert(content, {
                    wordwrap: false,
                    preserveNewlines: true,
                });
            }
            return content;
        }
    }
    
    // Handle object content
    if (typeof content === 'object') {
        if (content.version === 2 && content.content) {
            return tiptapToMarkdown(content.content);
        }
        if (content.type === 'doc') {
            return tiptapToMarkdown(content);
        }
    }
    
    return '';
}

/**
 * Convert TipTap JSON to Markdown
 * @param {object} doc - TipTap document
 * @returns {string} - Markdown content
 */
function tiptapToMarkdown(doc) {
    if (!doc || !doc.content) return '';
    
    function processNode(node) {
        if (node.type === 'text') {
            let text = node.text || '';
            // Apply marks
            if (node.marks) {
                for (const mark of node.marks) {
                    if (mark.type === 'bold') text = `**${text}**`;
                    if (mark.type === 'italic') text = `*${text}*`;
                    if (mark.type === 'code') text = `\`${text}\``;
                    if (mark.type === 'strike') text = `~~${text}~~`;
                    if (mark.type === 'link') text = `[${text}](${mark.attrs?.href || ''})`;
                }
            }
            return text;
        }
        
        if (node.type === 'hardBreak') return '  \n';
        
        if (node.type === 'paragraph') {
            const content = node.content ? node.content.map(processNode).join('') : '';
            return content + '\n\n';
        }
        
        if (node.type === 'heading') {
            const level = node.attrs?.level || 1;
            const content = node.content ? node.content.map(processNode).join('') : '';
            return '#'.repeat(level) + ' ' + content + '\n\n';
        }
        
        if (node.type === 'bulletList') {
            const items = node.content || [];
            return items.map(item => {
                const content = item.content ? item.content.map(processNode).join('').trim() : '';
                return '- ' + content;
            }).join('\n') + '\n\n';
        }
        
        if (node.type === 'orderedList') {
            const items = node.content || [];
            return items.map((item, i) => {
                const content = item.content ? item.content.map(processNode).join('').trim() : '';
                return `${i + 1}. ` + content;
            }).join('\n') + '\n\n';
        }
        
        if (node.type === 'listItem') {
            return node.content ? node.content.map(processNode).join('') : '';
        }
        
        if (node.type === 'codeBlock') {
            const lang = node.attrs?.language || '';
            const content = node.content ? node.content.map(processNode).join('') : '';
            return '```' + lang + '\n' + content + '\n```\n\n';
        }
        
        if (node.type === 'blockquote') {
            const content = node.content ? node.content.map(processNode).join('') : '';
            return '> ' + content.replace(/\n\n/g, '\n> ').trim() + '\n\n';
        }
        
        if (node.type === 'horizontalRule') return '---\n\n';
        
        if (node.type === 'image') {
            const src = node.attrs?.src || '';
            const alt = node.attrs?.alt || 'image';
            return `![${alt}](${src})\n\n`;
        }
        
        if (node.type === 'table') {
            const rows = node.content || [];
            let md = '';
            rows.forEach((row, rowIndex) => {
                const cells = row.content || [];
                const rowContent = cells.map(cell => {
                    return cell.content ? cell.content.map(processNode).join('').trim() : '';
                }).join(' | ');
                md += '| ' + rowContent + ' |\n';
                // Add header separator after first row
                if (rowIndex === 0) {
                    md += '| ' + cells.map(() => '---').join(' | ') + ' |\n';
                }
            });
            return md + '\n';
        }
        
        // Default: process children
        if (node.content) {
            return node.content.map(processNode).join('');
        }
        
        return '';
    }
    
    return processNode(doc).trim();
}

/**
 * Export note as Markdown file
 * @param {object} note - Note object
 */
export function exportAsMarkdown(note) {
    const filename = generateFilename(note) + '.md';
    const title = note.title || note.domain || 'Untitled';
    const url = note.url || note.key;
    const content = contentToMarkdown(note.content);
    
    let md = `# ${title}\n\n`;
    if (url && url.startsWith('http')) {
        md += `**URL:** [${url}](${url})\n\n`;
    }
    md += `**Created:** ${formatTimestamp(note.createdAt)}\n`;
    md += `**Updated:** ${formatTimestamp(note.updatedAt)}\n\n`;
    md += '---\n\n';
    md += content;
    
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    saveAs(blob, filename);
}

/**
 * Export note as PDF file
 * @param {object} note - Note object
 */
export function exportAsPdf(note) {
    const filename = generateFilename(note) + '.pdf';
    const title = note.title || note.domain || 'Untitled';
    const url = note.url || note.key || '';
    const content = getPlainTextContent(note.content);
    
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);
    let y = margin;
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    const titleLines = doc.splitTextToSize(title, contentWidth);
    doc.text(titleLines, margin, y);
    y += titleLines.length * 8 + 5;
    
    // URL
    if (url && url.startsWith('http')) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        const urlLines = doc.splitTextToSize(`URL: ${url}`, contentWidth);
        doc.text(urlLines, margin, y);
        y += urlLines.length * 5;
    }
    
    // Timestamps
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128);
    doc.text(`Created: ${formatTimestamp(note.createdAt)}`, margin, y);
    y += 5;
    doc.text(`Updated: ${formatTimestamp(note.updatedAt)}`, margin, y);
    y += 10;
    
    // Horizontal line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 10;
    
    // Content
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    
    const lines = doc.splitTextToSize(content, contentWidth);
    
    for (const line of lines) {
        if (y > pageHeight - margin) {
            doc.addPage();
            y = margin;
        }
        doc.text(line, margin, y);
        y += 6;
    }
    
    doc.save(filename);
}

/**
 * Export note as DOCX file
 * @param {object} note - Note object
 */
export async function exportAsDocx(note) {
    const filename = generateFilename(note) + '.docx';
    const title = note.title || note.domain || 'Untitled';
    const url = note.url || note.key || '';
    const content = getPlainTextContent(note.content);
    
    const children = [];
    
    // Title
    children.push(
        new Paragraph({
            text: title,
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 200 },
        })
    );
    
    // URL as hyperlink
    if (url && url.startsWith('http')) {
        children.push(
            new Paragraph({
                children: [
                    new TextRun({ text: 'URL: ', bold: true }),
                    new ExternalHyperlink({
                        children: [
                            new TextRun({
                                text: url,
                                style: 'Hyperlink',
                            }),
                        ],
                        link: url,
                    }),
                ],
                spacing: { after: 100 },
            })
        );
    }
    
    // Timestamps
    children.push(
        new Paragraph({
            children: [
                new TextRun({ text: 'Created: ', bold: true }),
                new TextRun({ text: formatTimestamp(note.createdAt) }),
            ],
            spacing: { after: 50 },
        })
    );
    
    children.push(
        new Paragraph({
            children: [
                new TextRun({ text: 'Updated: ', bold: true }),
                new TextRun({ text: formatTimestamp(note.updatedAt) }),
            ],
            spacing: { after: 200 },
        })
    );
    
    // Separator
    children.push(
        new Paragraph({
            text: '',
            border: {
                bottom: { color: 'CCCCCC', size: 1, space: 1, style: 'single' },
            },
            spacing: { after: 200 },
        })
    );
    
    // Content - split by paragraphs
    const paragraphs = content.split(/\n\n+/);
    for (const para of paragraphs) {
        if (para.trim()) {
            // Check if it's a heading
            const headingMatch = para.match(/^(#{1,6})\s+(.+)$/);
            if (headingMatch) {
                const level = Math.min(headingMatch[1].length, 6);
                const headingLevels = [
                    HeadingLevel.HEADING_1,
                    HeadingLevel.HEADING_2,
                    HeadingLevel.HEADING_3,
                    HeadingLevel.HEADING_4,
                    HeadingLevel.HEADING_5,
                    HeadingLevel.HEADING_6,
                ];
                children.push(
                    new Paragraph({
                        text: headingMatch[2],
                        heading: headingLevels[level - 1],
                        spacing: { after: 100 },
                    })
                );
            } else {
                children.push(
                    new Paragraph({
                        text: para.replace(/\n/g, ' ').trim(),
                        spacing: { after: 100 },
                    })
                );
            }
        }
    }
    
    const doc = new Document({
        sections: [{
            properties: {},
            children,
        }],
    });
    
    const blob = await Packer.toBlob(doc);
    saveAs(blob, filename);
}

/**
 * Export note in specified format
 * @param {object} note - Note object
 * @param {string} format - Export format: 'txt', 'json', 'markdown', 'pdf', 'docx'
 */
export async function exportNote(note, format) {
    switch (format.toLowerCase()) {
        case 'txt':
            exportAsTxt(note);
            break;
        case 'json':
            exportAsJson(note);
            break;
        case 'markdown':
        case 'md':
            exportAsMarkdown(note);
            break;
        case 'pdf':
            exportAsPdf(note);
            break;
        case 'docx':
            await exportAsDocx(note);
            break;
        default:
            console.error(`Unknown export format: ${format}`);
    }
}
